import React, { useState, useEffect } from "react";
import Nav from "../shared/Nav";
import Footer from "../shared/Footer";
import "../assets/css/variables.css";
import "../assets/css/global.css";

const BASE_URL =
  process.env.REACT_APP_API_URL || "https://accelia-backend.onrender.com";

/* ─── Static Data ──────────────────────────────────────── */
const REGIONS = [
  {
    id: "asia",
    label: "Asia-Pacific",
    flag: "🌏",
    heading: "Asia-Pacific Offices",
    desc: "Our regional hub in Singapore coordinates with offices in India, South Korea, and Australia to deliver cost-effective, regulatory-compliant clinical operations.",
    offices: [
      {
        country: "Singapore",
        detail: "100 Cross Street, Singapore 048421",
        flag: "🇸🇬",
      },
      { country: "India", detail: "Bangalore, Hyderabad", flag: "🇮🇳" },
      { country: "South Korea", detail: "Seoul", flag: "🇰🇷" },
      { country: "Australia", detail: "Sydney, Melbourne", flag: "🇦🇺" },
    ],
    mapSrc:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d235145.72648967118!2d88.18362857616464!3d22.535564861360903!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f882db4908f667%3A0x43e330e68f6c2cbc!2sKolkata%2C%20West%20Bengal!5e0!3m2!1sen!2sin!4v1689940126574!5m2!1sen!2sin",
    color: "#1B8FA6",
  },
  {
    id: "europe",
    label: "Europe",
    flag: "🌍",
    heading: "European Offices",
    desc: "Strategically located in Western and Eastern Europe, our offices provide agile solutions for multicenter clinical trials.",
    offices: [
      { country: "Germany", detail: "Frankfurt", flag: "🇩🇪" },
      { country: "United Kingdom", detail: "London", flag: "🇬🇧" },
      { country: "Poland", detail: "Warsaw", flag: "🇵🇱" },
    ],
    mapSrc:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d100408.04921141723!2d8.5511379!3d50.1109221!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x47bda9c0f0a1e481%3A0x6e5e4571967b2d71!2sFrankfurt%2C%20Germany!5e0!3m2!1sen!2sde!4v1689940074170!5m2!1sen!2sde",
    color: "#2A6496",
  },
  {
    id: "north",
    label: "North America",
    flag: "🌎",
    heading: "North American Offices",
    desc: "Accelia's presence in North America ensures alignment with FDA regulations and deep access to patient populations.",
    offices: [
      { country: "USA", detail: "Boston, San Francisco", flag: "🇺🇸" },
      { country: "Canada", detail: "Toronto", flag: "🇨🇦" },
    ],
    mapSrc:
      "https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2948.708906231059!2d-71.05888098454292!3d42.3600829791866!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89e370aaf30425e3%3A0x62f2aa26de0d13b7!2sBoston%2C%20MA!5e0!3m2!1sen!2sus!4v1689940126574!5m2!1sen!2sus",
    color: "#5B4A8A",
  },
];

const ICON_MAP = { building: "🏢", pin: "📍", office: "🏬", lab: "🔬" };

const FALLBACK_LOCATIONS = [
  {
    _id: "f1",
    city: "Kolkata",
    state: "West Bengal",
    tagline: "Headquarters – Strategic Hub",
    isHQ: true,
    status: "Active",
    icon: "building",
  },
  {
    _id: "f2",
    city: "West Bengal",
    state: "West Bengal",
    tagline: "All Regions Covered",
    isHQ: false,
    status: "Active",
    icon: "pin",
  },
  {
    _id: "f3",
    city: "Assam",
    state: "Assam",
    tagline: "North-East Presence",
    isHQ: false,
    status: "Active",
    icon: "pin",
  },
  {
    _id: "f4",
    city: "Bhubaneswar",
    state: "Odisha",
    tagline: "Odisha Operations",
    isHQ: false,
    status: "Active",
    icon: "pin",
  },
  {
    _id: "f5",
    city: "Bihar",
    state: "Bihar",
    tagline: "Eastern Zone Coverage",
    isHQ: false,
    status: "Active",
    icon: "pin",
  },
  {
    _id: "f6",
    city: "Uttar Pradesh",
    state: "Uttar Pradesh",
    tagline: "Northern Operations",
    isHQ: false,
    status: "Active",
    icon: "pin",
  },
  {
    _id: "f7",
    city: "Delhi NCR",
    state: "Delhi",
    tagline: "Capital Region Hub",
    isHQ: false,
    status: "Active",
    icon: "office",
  },
];

/* ─── Skeleton ─────────────────────────────────────────── */
function LocSkeleton() {
  return (
    <>
      <style>{`
        @keyframes shimmer { to { background-position: -200% 0; } }
        .loc-skel {
          background: linear-gradient(90deg, #e8eef4 25%, #f5f7fa 50%, #e8eef4 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
          border-radius: 20px; height: 280px;
        }
      `}</style>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="loc-skel" />
      ))}
    </>
  );
}

/* ─── Main Component ───────────────────────────────────── */
export default function Locations() {
  const [activeTab, setActiveTab] = useState("asia");
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [usingFallback, setUsingFallback] = useState(false);

  const activeRegion = REGIONS.find((r) => r.id === activeTab);
  const hqLocation = locations.find((l) => l.isHQ);
  const nonHqLocations = locations.filter((l) => !l.isHQ);

  // Dynamic stats derived from locations data
  const uniqueStates = [
    ...new Set(locations.map((l) => l.state).filter(Boolean)),
  ];
  const activeCount = locations.filter(
    (l) => l.status === "Active" || l.isHQ,
  ).length;
  const statesCount = uniqueStates.length || locations.length;

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const res = await fetch(`${BASE_URL}/api/locations`, {
          headers: { Accept: "application/json" },
        });
        const contentType = res.headers.get("content-type") || "";
        if (!res.ok || !contentType.includes("application/json"))
          throw new Error(`Server returned ${res.status}`);
        const raw = await res.json();
        if (cancelled) return;
        const arr = Array.isArray(raw)
          ? raw
          : Array.isArray(raw.locations)
            ? raw.locations
            : Array.isArray(raw.data)
              ? raw.data
              : [];
        const active = arr
          .filter((l) => l.status === "Active" || l.isHQ)
          .sort((a, b) => (a.order ?? 99) - (b.order ?? 99));
        if (active.length === 0) {
          setLocations(FALLBACK_LOCATIONS);
          setUsingFallback(true);
        } else setLocations(active);
      } catch {
        if (!cancelled) {
          setLocations(FALLBACK_LOCATIONS);
          setUsingFallback(true);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <>
      <Nav />
      <style>{css}</style>

      <div className="lp-wrapper">
        {/* ══ HERO ══════════════════════════════════════════ */}
        <section className="lp-hero">
          <div className="lp-hero__noise" />
          <div className="lp-hero__inner">
            <span className="lp-badge">Our Locations</span>
            <h1 className="lp-hero__title">
              Pan-India Clinical
              <br />
              Presence
            </h1>
            <p className="lp-hero__sub">
              With Kolkata as our strategic hub, Accelia's presence spans across
              all regions of West Bengal and key locations including Assam,
              Bhubaneswar, Bihar, and Uttar Pradesh — driving clinical
              excellence at every site.
            </p>

            {/* ── Dynamic Stats ── */}
            <div className="lp-hero__stats">
              <div className="lp-stat">
                <span className="lp-stat__num">
                  {loading ? "—" : `${statesCount}+`}
                </span>
                <span className="lp-stat__label">Indian States</span>
              </div>
              <div className="lp-stat__divider" />
              <div className="lp-stat">
                <span className="lp-stat__num">3</span>
                <span className="lp-stat__label">Global Regions</span>
              </div>
              <div className="lp-stat__divider" />
              <div className="lp-stat">
                <span className="lp-stat__num">
                  {loading ? "—" : `${activeCount}+`}
                </span>
                <span className="lp-stat__label">Active Offices</span>
              </div>
            </div>
          </div>
          <div className="lp-hero__scroll">↓ Explore Locations</div>
        </section>

        {/* ══ INDIA OPERATIONS ══════════════════════════════ */}
        <section className="lp-section lp-section--light">
          <div className="lp-container">
            <div className="lp-section__header">
              <span className="lp-badge lp-badge--teal">India Operations</span>
              <h2 className="lp-section__title">Where We Operate in India</h2>
              <p className="lp-section__sub">
                Our primary operations are headquartered in Kolkata with a
                growing multi-state clinical network spanning Eastern, Northern,
                and Northeastern India.
              </p>
            </div>

            {usingFallback && !loading && (
              <div className="lp-notice">
                ⚠️ Showing default locations — live data unavailable
              </div>
            )}

            {/* HQ Card */}
            {(hqLocation || !loading) && (
              <div className="lp-hq-card">
                <div className="lp-hq-card__left">
                  <div className="lp-hq-card__badge">🏢 Headquarters</div>
                  <h3 className="lp-hq-card__city">
                    {hqLocation?.city || "Kolkata"},{" "}
                    {hqLocation?.state || "West Bengal"}
                  </h3>
                  <p className="lp-hq-card__desc">
                    Accelia Clinical Solutions is headquartered in{" "}
                    {hqLocation?.city || "Kolkata"},{" "}
                    {hqLocation?.state || "West Bengal"} — the clinical research
                    hub of Eastern India. Our leadership team coordinates all
                    operational, regulatory, and business functions from here.
                  </p>
                  <div className="lp-hq-card__contacts">
                    <a
                      href={`mailto:${hqLocation?.email || "acceliaclinicalsolution@gmail.com"}`}
                      className="lp-contact-chip"
                    >
                      <span className="lp-contact-chip__icon">✉</span>
                      <span>
                        {hqLocation?.email ||
                          "acceliaclinicalsolution@gmail.com"}
                      </span>
                    </a>
                    <a
                      href={`tel:${hqLocation?.phone || "+918282986162"}`}
                      className="lp-contact-chip"
                    >
                      <span className="lp-contact-chip__icon">📞</span>
                      <span>{hqLocation?.phone || "+91 8282986162"}</span>
                    </a>
                    {hqLocation?.address && (
                      <div className="lp-contact-chip">
                        <span className="lp-contact-chip__icon">📍</span>
                        <span>{hqLocation.address}</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="lp-hq-card__right">
                  <div className="lp-hq-card__map-label">Eastern India Hub</div>
                  <div className="lp-hq-card__visual">
                    <div className="lp-hq-pulse">
                      <div className="lp-hq-pulse__ring" />
                      <div className="lp-hq-pulse__dot" />
                    </div>
                    <svg
                      viewBox="0 0 200 200"
                      className="lp-hq-bg-svg"
                      aria-hidden="true"
                    >
                      <circle
                        cx="100"
                        cy="100"
                        r="90"
                        fill="none"
                        stroke="rgba(27,143,166,0.12)"
                        strokeWidth="1"
                      />
                      <circle
                        cx="100"
                        cy="100"
                        r="65"
                        fill="none"
                        stroke="rgba(27,143,166,0.08)"
                        strokeWidth="1"
                      />
                      <circle
                        cx="100"
                        cy="100"
                        r="40"
                        fill="none"
                        stroke="rgba(27,143,166,0.06)"
                        strokeWidth="1"
                      />
                    </svg>
                    <div className="lp-hq-card__city-label">Kolkata</div>
                  </div>
                </div>
              </div>
            )}

            {/* Regional Offices Grid */}
            <div className="lp-subsection-title">
              <span className="lp-dot" />
              Regional Offices Across India
            </div>

            <div className="lp-india-grid">
              {loading ? (
                <LocSkeleton />
              ) : (
                nonHqLocations.map((l, i) => (
                  <div
                    key={l._id || i}
                    className="lp-india-card"
                    style={{ animationDelay: `${i * 0.08}s` }}
                  >
                    {/* Image */}
                    <div className="lp-india-card__img">
                      {l.image ? (
                        <img src={l.image} alt={l.city} loading="lazy" />
                      ) : (
                        <div className="lp-india-card__img-fallback">
                          <span className="lp-india-card__img-icon">
                            {ICON_MAP[l.icon] || "📍"}
                          </span>
                        </div>
                      )}
                      {l.status === "Coming Soon" && (
                        <div className="lp-coming-soon-badge">Coming Soon</div>
                      )}
                    </div>

                    {/* Body */}
                    <div className="lp-india-card__body">
                      <div className="lp-india-card__top">
                        <div className="lp-india-card__icon-wrap">
                          {ICON_MAP[l.icon] || "📍"}
                        </div>
                        <div>
                          <h4 className="lp-india-card__city">{l.city}</h4>
                          <p className="lp-india-card__state">{l.state}</p>
                        </div>
                      </div>
                      <p className="lp-india-card__tagline">{l.tagline}</p>
                      <div className="lp-india-card__footer">
                        <span
                          className={`lp-status-dot lp-status-dot--${(l.status || "active").toLowerCase().replace(" ", "-")}`}
                        />
                        <span className="lp-status-label">
                          {l.status || "Active"}
                        </span>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </section>

        {/* ══ GLOBAL PRESENCE ═══════════════════════════════ */}
        <section className="lp-section lp-section--dark">
          <div className="lp-container">
            <div className="lp-section__header lp-section__header--dark">
              <span className="lp-badge lp-badge--gold">Global Presence</span>
              <h2 className="lp-section__title lp-section__title--light">
                Our Global Presence
              </h2>
              <p className="lp-section__sub lp-section__sub--light">
                With a strong international footprint, Accelia operates clinical
                research offices throughout Asia-Pacific, North America, and
                Europe.
              </p>
            </div>

            {/* Region Tabs */}
            <div className="lp-region-tabs">
              {REGIONS.map((r) => (
                <button
                  key={r.id}
                  className={`lp-region-tab${activeTab === r.id ? " lp-region-tab--active" : ""}`}
                  onClick={() => setActiveTab(r.id)}
                  style={{ "--tab-color": r.color }}
                >
                  <span className="lp-region-tab__flag">{r.flag}</span>
                  {r.label}
                </button>
              ))}
            </div>

            {/* Active Region Panel */}
            {activeRegion && (
              <div className="lp-region-panel" key={activeTab}>
                <div className="lp-region-panel__left">
                  <h3 className="lp-region-panel__title">
                    {activeRegion.heading}
                  </h3>
                  <p className="lp-region-panel__desc">{activeRegion.desc}</p>
                  <div className="lp-offices-list">
                    {activeRegion.offices.map((o, i) => (
                      <div key={i} className="lp-office-row">
                        <span className="lp-office-row__flag">{o.flag}</span>
                        <div>
                          <span className="lp-office-row__country">
                            {o.country}
                          </span>
                          <span className="lp-office-row__detail">
                            {o.detail}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="lp-region-panel__right">
                  <div className="lp-map-wrap">
                    <iframe
                      src={activeRegion.mapSrc}
                      width="100%"
                      height="100%"
                      style={{ border: 0 }}
                      allowFullScreen=""
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      title={activeRegion.heading}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>
      </div>

      <Footer />
    </>
  );
}

/* ─── Styles ────────────────────────────────────────────── */
const css = `
  /* ── Base ─────────────────────────────────────────────── */
  .lp-wrapper { font-family: 'Lato', sans-serif; color: #0B1F3A; }
  .lp-container { max-width: 1200px; margin: 0 auto; padding: 0 32px; }

  /* ── Badge ────────────────────────────────────────────── */
  .lp-badge {
    display: inline-block; padding: 5px 18px; border-radius: 100px;
    font-size: 11px; font-weight: 700; letter-spacing: 1.8px; text-transform: uppercase;
    background: rgba(27,143,166,0.15); color: #1B8FA6; border: 1px solid rgba(27,143,166,0.3);
    margin-bottom: 18px;
  }
  .lp-badge--teal { background: rgba(27,143,166,0.1); color: #1B8FA6; border-color: rgba(27,143,166,0.25); }
  .lp-badge--gold { background: rgba(184,148,68,0.15); color: #C9A84C; border-color: rgba(184,148,68,0.3); }

  /* ── Hero ─────────────────────────────────────────────── */
  .lp-hero {
    position: relative; background: #0B1F3A;
    padding: 120px 32px 100px; text-align: center; overflow: hidden;
  }
  .lp-hero__noise {
    position: absolute; inset: 0; pointer-events: none; opacity: 0.035;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E");
    background-size: 200px 200px;
  }
  .lp-hero::before {
    content: ''; position: absolute; top: -60px; left: 50%; transform: translateX(-50%);
    width: 700px; height: 400px;
    background: radial-gradient(ellipse at center, rgba(27,143,166,0.18) 0%, transparent 70%);
  }
  .lp-hero__inner { position: relative; max-width: 740px; margin: 0 auto; }
  .lp-hero__title {
    font-family: 'Cormorant Garamond', serif; font-size: clamp(40px, 5vw, 68px);
    font-weight: 700; color: #fff; line-height: 1.12; margin-bottom: 20px; margin-top: 0;
  }
  .lp-hero__sub { color: rgba(255,255,255,0.6); font-size: 17px; line-height: 1.8; margin-bottom: 48px; }
  .lp-hero__stats {
    display: inline-flex; align-items: center;
    border: 1px solid rgba(255,255,255,0.1); border-radius: 16px;
    background: rgba(255,255,255,0.04); backdrop-filter: blur(8px);
    padding: 24px 40px;
  }
  .lp-stat { text-align: center; padding: 0 32px; }
  .lp-stat__num {
    display: block; font-family: 'Cormorant Garamond', serif;
    font-size: 36px; font-weight: 700; color: #1B8FA6; line-height: 1;
  }
  .lp-stat__label { display: block; font-size: 11px; color: rgba(255,255,255,0.5); text-transform: uppercase; letter-spacing: 1px; margin-top: 4px; }
  .lp-stat__divider { width: 1px; height: 48px; background: rgba(255,255,255,0.12); flex-shrink: 0; }
  .lp-hero__scroll {
    position: relative; margin-top: 48px; font-size: 12px; letter-spacing: 2px;
    text-transform: uppercase; color: rgba(255,255,255,0.3);
  }

  /* ── Section ──────────────────────────────────────────── */
  .lp-section { padding: 96px 0; }
  .lp-section--light { background: #F8FAFB; }
  .lp-section--dark { background: #0B1F3A; }
  .lp-section__header { text-align: center; margin-bottom: 56px; }
  .lp-section__title {
    font-family: 'Cormorant Garamond', serif; font-size: clamp(28px, 3.5vw, 46px);
    font-weight: 700; color: #0B1F3A; margin: 0 0 14px;
  }
  .lp-section__title--light { color: #fff; }
  .lp-section__sub { font-size: 16px; color: #5E6E82; max-width: 580px; margin: 0 auto; line-height: 1.8; }
  .lp-section__sub--light { color: rgba(255,255,255,0.55); }

  /* ── HQ Card ──────────────────────────────────────────── */
  .lp-hq-card {
    display: grid; grid-template-columns: 1fr 340px; gap: 0;
    background: #fff; border-radius: 20px; overflow: hidden;
    box-shadow: 0 4px 32px rgba(11,31,58,0.09), 0 1px 4px rgba(11,31,58,0.06);
    border: 1px solid rgba(11,31,58,0.07); margin-bottom: 64px;
  }
  @media (max-width: 768px) { .lp-hq-card { grid-template-columns: 1fr; } }
  .lp-hq-card__left { padding: 44px 48px; }
  .lp-hq-card__badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 6px 16px; border-radius: 100px; font-size: 11px; font-weight: 700;
    letter-spacing: 1px; text-transform: uppercase;
    background: rgba(27,143,166,0.08); color: #1B8FA6; margin-bottom: 16px;
  }
  .lp-hq-card__city {
    font-family: 'Cormorant Garamond', serif; font-size: 32px; font-weight: 700;
    color: #0B1F3A; margin: 0 0 14px;
  }
  .lp-hq-card__desc { font-size: 15px; color: #5E6E82; line-height: 1.78; margin-bottom: 28px; max-width: 500px; }
  .lp-hq-card__contacts { display: flex; flex-direction: column; gap: 10px; }
  .lp-contact-chip {
    display: inline-flex; align-items: center; gap: 10px;
    font-size: 14px; color: #3A5068; text-decoration: none;
    padding: 9px 16px; border-radius: 10px;
    background: #F0F4F8; border: 1px solid #E2E8EF;
    transition: background 0.2s, border-color 0.2s; width: fit-content;
  }
  .lp-contact-chip:hover { background: #E2ECF5; border-color: #C3D4E3; }
  .lp-contact-chip__icon { font-size: 15px; flex-shrink: 0; }
  .lp-hq-card__right {
    background: linear-gradient(135deg, #0B2640 0%, #0e3352 100%);
    display: flex; flex-direction: column; align-items: center; justify-content: center;
    padding: 40px; position: relative; overflow: hidden;
  }
  .lp-hq-card__map-label {
    font-size: 10px; letter-spacing: 2px; text-transform: uppercase;
    color: rgba(255,255,255,0.35); margin-bottom: 24px;
  }
  .lp-hq-card__visual { position: relative; width: 160px; height: 160px; display: flex; align-items: center; justify-content: center; }
  .lp-hq-bg-svg { position: absolute; inset: 0; width: 100%; height: 100%; }
  .lp-hq-pulse { position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); }
  @keyframes pulseRing { 0% { transform: scale(0.6); opacity: 0.8; } 100% { transform: scale(2.4); opacity: 0; } }
  .lp-hq-pulse__ring {
    width: 32px; height: 32px; border-radius: 50%;
    border: 2px solid rgba(27,143,166,0.6);
    animation: pulseRing 2.2s ease-out infinite;
  }
  .lp-hq-pulse__dot {
    position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%);
    width: 10px; height: 10px; border-radius: 50%; background: #1B8FA6;
    box-shadow: 0 0 0 3px rgba(27,143,166,0.2);
  }
  .lp-hq-card__city-label {
    font-family: 'Cormorant Garamond', serif; font-size: 22px; font-weight: 700;
    color: #fff; margin-top: 16px; text-align: center; letter-spacing: 1px;
  }

  /* ── Subsection title ─────────────────────────────────── */
  .lp-subsection-title {
    display: flex; align-items: center; gap: 10px;
    font-size: 13px; font-weight: 700; letter-spacing: 1.5px; text-transform: uppercase;
    color: #5E6E82; margin-bottom: 28px;
  }
  .lp-dot { width: 6px; height: 6px; border-radius: 50%; background: #1B8FA6; flex-shrink: 0; }

  /* ── India Grid ───────────────────────────────────────── */
  .lp-india-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
    gap: 24px;
  }

  @keyframes fadeUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }

  .lp-india-card {
    background: #fff;
    border-radius: 20px;
    overflow: hidden;
    border: 1px solid #E8EEF4;
    box-shadow: 0 2px 16px rgba(11,31,58,0.06);
    transition: transform 0.25s ease, box-shadow 0.25s ease;
    animation: fadeUp 0.45s ease both;
    display: flex;
    flex-direction: column;
  }
  .lp-india-card:hover {
    transform: translateY(-6px);
    box-shadow: 0 12px 40px rgba(11,31,58,0.13);
  }

  /* Card Image */
  .lp-india-card__img {
    position: relative;
    height: 200px;
    overflow: hidden;
    background: #e8eef4;
    flex-shrink: 0;
  }
  .lp-india-card__img img {
    width: 100%; height: 100%; object-fit: cover;
    transition: transform 0.4s ease;
  }
  .lp-india-card:hover .lp-india-card__img img { transform: scale(1.05); }

  .lp-india-card__img-fallback {
    width: 100%; height: 100%;
    background: linear-gradient(135deg, #0B2640 0%, #1B8FA6 100%);
    display: flex; align-items: center; justify-content: center;
  }
  .lp-india-card__img-icon { font-size: 56px; filter: drop-shadow(0 4px 12px rgba(0,0,0,0.3)); }

  .lp-coming-soon-badge {
    position: absolute; top: 12px; right: 12px;
    padding: 4px 12px; border-radius: 20px; font-size: 10px; font-weight: 700; letter-spacing: 0.5px;
    background: rgba(245,158,11,0.9); color: #fff;
  }

  /* Card Body */
  .lp-india-card__body {
    padding: 22px 24px 20px;
    display: flex; flex-direction: column; gap: 12px;
    flex: 1;
  }
  .lp-india-card__top {
    display: flex; align-items: center; gap: 14px;
  }
  .lp-india-card__icon-wrap {
    width: 44px; height: 44px; border-radius: 12px;
    background: rgba(27,143,166,0.08);
    display: flex; align-items: center; justify-content: center;
    font-size: 22px; flex-shrink: 0;
  }
  .lp-india-card__city {
    font-family: 'Cormorant Garamond', serif;
    font-size: 20px; font-weight: 700; color: #0B1F3A;
    margin: 0 0 2px;
  }
  .lp-india-card__state {
    font-size: 11px; color: #1B8FA6; font-weight: 700;
    margin: 0; text-transform: uppercase; letter-spacing: 0.8px;
  }
  .lp-india-card__tagline {
    font-size: 13.5px; color: #6B7E95; margin: 0; line-height: 1.6;
    padding-top: 4px;
    border-top: 1px solid #F0F4F8;
  }
  .lp-india-card__footer {
    display: flex; align-items: center; gap: 7px; margin-top: auto;
  }
  .lp-status-dot {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0;
  }
  .lp-status-dot--active { background: #22c55e; box-shadow: 0 0 0 2px rgba(34,197,94,0.2); }
  .lp-status-dot--coming-soon { background: #f59e0b; }
  .lp-status-label { font-size: 12px; color: #8A9BB0; font-weight: 600; }

  /* ── Notice ───────────────────────────────────────────── */
  .lp-notice {
    display: inline-flex; align-items: center; gap: 8px;
    padding: 8px 16px; border-radius: 10px; margin-bottom: 24px;
    background: rgba(245,158,11,0.08); border: 1px solid rgba(245,158,11,0.2);
    color: #b45309; font-size: 12px; font-weight: 600;
  }

  /* ── Global Tabs ──────────────────────────────────────── */
  .lp-region-tabs { display: flex; gap: 10px; margin-bottom: 40px; flex-wrap: wrap; }
  .lp-region-tab {
    display: flex; align-items: center; gap: 8px;
    padding: 11px 24px; border-radius: 100px;
    background: rgba(255,255,255,0.07); border: 1px solid rgba(255,255,255,0.12);
    color: rgba(255,255,255,0.55); font-size: 14px; font-weight: 600; cursor: pointer;
    transition: all 0.22s ease;
  }
  .lp-region-tab:hover { background: rgba(255,255,255,0.11); color: rgba(255,255,255,0.8); }
  .lp-region-tab--active {
    background: var(--tab-color, #1B8FA6) !important;
    border-color: transparent !important; color: #fff !important;
    box-shadow: 0 4px 16px rgba(0,0,0,0.25);
  }
  .lp-region-tab__flag { font-size: 16px; }

  /* ── Region Panel ─────────────────────────────────────── */
  @keyframes regionIn { from { opacity: 0; transform: translateX(-12px); } to { opacity: 1; transform: translateX(0); } }
  .lp-region-panel {
    display: grid; grid-template-columns: 1fr 1.2fr; gap: 48px;
    animation: regionIn 0.3s ease;
  }
  @media (max-width: 768px) { .lp-region-panel { grid-template-columns: 1fr; } }
  .lp-region-panel__title {
    font-family: 'Cormorant Garamond', serif; font-size: 30px; font-weight: 700;
    color: #fff; margin: 0 0 14px;
  }
  .lp-region-panel__desc { font-size: 15px; color: rgba(255,255,255,0.55); line-height: 1.8; margin-bottom: 28px; }
  .lp-offices-list { display: flex; flex-direction: column; gap: 12px; }
  .lp-office-row {
    display: flex; align-items: center; gap: 14px;
    padding: 14px 18px; border-radius: 12px;
    background: rgba(255,255,255,0.05); border: 1px solid rgba(255,255,255,0.09);
  }
  .lp-office-row__flag { font-size: 20px; flex-shrink: 0; }
  .lp-office-row__country { display: block; font-size: 14px; font-weight: 700; color: #fff; margin-bottom: 2px; }
  .lp-office-row__detail { display: block; font-size: 13px; color: rgba(255,255,255,0.45); }
  .lp-map-wrap {
    height: 380px; border-radius: 16px; overflow: hidden;
    border: 1px solid rgba(255,255,255,0.1);
    box-shadow: 0 8px 32px rgba(0,0,0,0.3);
  }
  .lp-map-wrap iframe { width: 100%; height: 100%; display: block; }

  /* ── Responsive ───────────────────────────────────────── */
  @media (max-width: 640px) {
    .lp-hero { padding: 80px 20px 72px; }
    .lp-hero__stats { flex-direction: column; gap: 16px; padding: 20px; }
    .lp-stat__divider { width: 40px; height: 1px; }
    .lp-container { padding: 0 20px; }
    .lp-hq-card__left { padding: 28px 24px; }
    .lp-india-grid { grid-template-columns: repeat(auto-fill, minmax(240px, 1fr)); gap: 16px; }
    .lp-india-card__img { height: 160px; }
  }
`;
